import { renderRichText, applyLocaleToUrl } from "@/utils/formatters";
import React from "react";
import { useRouter } from "next/router";

/**
 * @typedef {Object} ContactItem
 * @property {"heading" | "contact"} [type] - Type of item (heading or contact)
 * @property {string} label - Label text (e.g. "Phone", "Vietnam")
 * @property {string} [value] - Value for contact items (e.g. phone number, email)
 * @property {string} [href] - Optional link (tel:, mailto:, etc.)
 */

/**
 * @typedef {Object} ResourceLink
 * @property {string} label - Link text
 * @property {string} href - URL
 */

/**
 * @typedef {Object} CTAButton
 * @property {string} label - Button text
 * @property {string} href - URL
 */

/**
 * @typedef {Object} ContactUsLandingPageData
 * @property {string} title - Page title
 * @property {string} description - HTML string (can include anchor tags)
 * @property {string} officeTitle - Office section heading
 * @property {ContactItem[]} officeDetails - Office details list
 * @property {string} resourcesTitle - Resources section title
 * @property {ResourceLink[]} resourcesLinks - List of resource links
 * @property {CTAButton[]} ctaButtons - CTA buttons list
 */
/**
 * @param {{ data: ContactUsLandingPageData }} props
 */
function ContactUs({ data, descriptionRow }) {
  const { locale } = useRouter();

  const {
    pageHeading: title,
    officeTitle,
    officeDetails,
    resourcesTitle,
    resourcesLinks,
    ctaButtons,
  } = data;
  const description = renderRichText(descriptionRow, locale);

  return (
    <section className="max-w-[980px] mx-auto px-4 py-8">
      {/* Title */}
      <div className="max-w-6xl mx-auto text-center">
        <h1 className="text-[45px] md:text-[60px] font-![HelveticaLight,Helvetica,sans-serif] text-gray-800">
          {title}
        </h1>
        <div className="mt-10 border-t border-black-300"></div>
      </div>

      {/* Layout */}
      <div className="max-w-6xl mx-auto mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
        {/* LEFT */}
        <div className="md:col-span-8 ">
          <p className="text-sm font-normal text-gray-800 leading-relaxed font-['HelveticaRoman','Helvetica',sans-serif]">
            {description}
          </p>

          <h2 className="mt-6 text-xl md:text-2xl  font-['HelveticaRoman','Helvetica',sans-serif] text-gray-800">
            {officeTitle}
          </h2>

          <div className="my-8 md:w-72 border-t border-gray-300"></div>

          <div className="w-full md:w-1/2 mt-5 text-sm text-gray-800 space-y-2">
            {officeDetails?.map((item, index) => {
              const classBold = item?.showBold
                ? "font-[helveticaBold,_Helvetica,_sans-serif]"
                : "font-[HelveticaRoman,_Helvetica,_sans-serif]";
              if (item.type === "heading") {
                return (
                  <p
                    key={index}
                    className={`font-bold text-sm text-gray-900 font-['HelveticaBold','Helvetica',sans-serif]`}
                  >
                    {item.label}
                  </p>
                );
              }

              if (item?.type === "contact") {
                return (
                  <p
                    key={index}
                    className="text-gray-900 my-0 font-[HelveticaRoman,_Helvetica,_sans-serif]"
                  >
                    {item.href ? (
                      <>
                        <span className={classBold}>{item?.label}</span>:{" "}
                        <a
                          href={item.href}
                          className="text-[#337ab7] no-underline hover:!no-underline"
                        >
                          {item.value}
                        </a>
                      </>
                    ) : (
                      <>
                        <span className={classBold}>{item?.label}</span>:{" "}
                        {item?.value}
                      </>
                    )}
                  </p>
                );
              }

              return (
                item?.type !== "contact" && (
                  <p
                    key={index}
                    className="text-gray-900 pr-[30px] pb-[10px] font-[HelveticaRoman,_Helvetica,_sans-serif]"
                  >
                    {item?.label}
                  </p>
                )
              );
            })}
          </div>
        </div>

        {/* RIGHT */}
        <div className="flex flex-col md:col-span-4">
          {/* Divider for mobile */}
          <div className="block md:hidden my-6 border-t border-gray-300"></div>

          <h3 className="text-base md:text-2xl font-['HelveticaRoman','Helvetica',sans-serif] text-gray-800 uppercase">
            {resourcesTitle}
          </h3>

          <ul className="mt-3 mb-8 text-sm list-disc">
            {resourcesLinks?.map((link, index) => (
              <li key={index} className="ml-12">
                <a
                  href={
                    link?.href?.startsWith("http")
                      ? link?.href
                      : `/${locale}${link?.href}`
                  }
                  target={link?.href?.startsWith("http") ? "_blank" : "_self"}
                  className="!font-['HelveticaRoman','Helvetica',sans-serif] text-[#337ab7] no-underline hover:!no-underline"
                >
                  {link?.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Buttons */}
          <div className="mt-6 flex flex-col space-y-15">
            {ctaButtons?.map((btn, index) => (
              <a
                key={index}
                href={
                  btn?.href?.startsWith("http")
                    ? btn?.href
                    : `/${locale}${btn?.href}`
                }
                target={btn?.href?.startsWith("http") ? "_blank" : "_self"}
                className=" cursor-pointer h-[41px] block w-full text-center bg-[#364573] text-white py-2 align-middle rounded no-underline !text-white hover:!no-underline transition-colors"
              >
                {btn?.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContactUs;
